import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Skeleton,
  Stack,
  Button,
  Checkbox
} from "@mui/material";
import SnippetModal from "../../luminaire/Conversations/SnippetModal";
import Modal from "../Modal/Modal";
import classes from "./GenericTable.module.css"

function PaginatedGenericTable({
  data = [],
  title,
  sources,
  rawData,
  maxPageHeight = 650,
  loading,
  error,
  open,
  setOpen,
  exportReport,
  id
}) {
  const [pages, setPages] = useState([]);
  const [isCalculating, setIsCalculating] = useState(true);
  const measureRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      setIsCalculating(false);
      return;
    }

    setTimeout(calculatePages, 100);
  }, [data]);

  const calculatePages = () => {
    if (!measureRef.current) return;

    const tableBodyRows =
      measureRef.current.querySelectorAll("tbody tr");

    const headerHeight =
      measureRef.current.querySelector("thead")?.offsetHeight || 60;

    let currentPage = [];
    let currentHeight = headerHeight;
    const calculatedPages = [];

    tableBodyRows.forEach((row, index) => {
      const rowHeight = row.offsetHeight;
      const safetyMargin = 40;

      if (
        currentHeight + rowHeight + safetyMargin > maxPageHeight &&
        currentPage.length > 0
      ) {
        calculatedPages.push([...currentPage]);
        currentPage = [index];
        currentHeight = headerHeight + rowHeight;
      } else {
        currentPage.push(index);
        currentHeight += rowHeight;
      }
    });

    if (currentPage.length) {
      calculatedPages.push(currentPage);
    }

    setPages(calculatedPages);
    setIsCalculating(false);
  };

  /* ---------- hidden measurement table ---------- */
  const renderMeasurementTable = () => (
    <div
      ref={measureRef}
      style={{
        position: "absolute",
        visibility: "hidden",
        width: "1100px",
        padding: "16px",
      }}
    >
      <GenericTable
        data={data}
        title={title}
        sources={sources}
        rawData={rawData}
        loading={loading}
        error={error}
        open={open}
        setOpen={setOpen}
        exportReport={exportReport}
        id={id}
      />
    </div>
  );

  if (isCalculating) {
    return (
      <>
        {renderMeasurementTable()}
        <Typography sx={{ p: 4 }}>Calculating page layout…</Typography>
      </>
    );
  }

  if (!pages.length) {
    return <Typography>No data available</Typography>;
  }

  return (
    <div>
      {pages.map((pageIndexes, pageIndex) => (
        <div
          key={pageIndex}
          className={classes.conversationreportpage}
          // data-report-page="conversation"
          style={{
            width: "1100px",
            minHeight: `${maxPageHeight}px`,
            maxHeight: `${maxPageHeight}px`,
            padding: "16px",
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            marginBottom: "30px",
            pageBreakAfter:
              pageIndex < pages.length - 1 ? "always" : "auto",
          }}
        >
          <GenericTable
            data={pageIndexes.map(i => data[i])}
            title={title}
            sources={sources}
            rawData={rawData}
            loading={false}
            open={false}
            setOpen={() => { }}
            exportReport={null}
            id={`${title}_page_${pageIndex}`}
            hideTitle={true}
            hideReportViewButton={true}
          />
        </div>
      ))}
    </div>
  );
}


const GenericTable = memo(function GenericTable({
  data = [],
  loading = false,
  error = null,
  title = "",
  maxHeight = 700,
  sources = null,
  open, setOpen, rawData,
  exportReport = () => null,
  id,
  hideTitle = false,
  hideReportViewButton = false,
  showReportCheckbox = false,
  isChecked = false,
  onReportToggle,
  isFirstPdfTable
}) {
  const [showReportModal, setShowReportModal] = useState(false);
  const isReporting = new URLSearchParams(window.location.search).get('isReporting') === 'true';

  // Check if sources exist at row level in data
  const hasRowLevelSources = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;

    return data.some(item =>
      item &&
      typeof item === 'object' &&
      'sources' in item &&
      Array.isArray(item.sources) &&
      item.sources.length > 0
    );
  }, [data]);

  const isSourcesArray =
    Array.isArray(sources) && sources.every((item) => typeof item === "string");

  const isStringArray = data.length > 0 && typeof data[0] === "string";
  const normalizedData = isStringArray
    ? data.map((item) => ({ Name: item }))
    : data;

  const columnKeys =
    normalizedData.length > 0
      ? Object.keys(normalizedData[0]).filter(key => key !== 'sources') // Exclude sources from column keys
      : [];

  const LoadingSkeleton = () => (
    <div className="p-3">
      <Skeleton variant="rectangular" height={30} />
      {Array(10)
        .fill(0)
        .map((_, rowIndex) => (
          <div
            key={rowIndex}
            style={{ display: "flex", gap: "1rem", marginTop: 10 }}
          >
            {columnKeys.map((_, colIndex) => (
              <Skeleton
                variant="rectangular"
                width={`${90 / columnKeys.length}%`}
                height={20}
                key={colIndex}
              />
            ))}
          </div>
        ))}
    </div>
  );
  const handleCheckboxChange = useCallback(() => {
    onReportToggle?.();
  }, [onReportToggle]);

  const formatHeader = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <>
      <Card
        sx={{
          m: 3,
          boxShadow: 3,
          borderRadius: 4,
          // '@media print': {
          //   pageBreakInside: 'avoid',
          //   breakInside: 'avoid',
          //   boxShadow: 'none',
          //   m: 1
          // }
          '@media print': {
            pageBreakInside: isFirstPdfTable ? 'auto' : 'avoid',
            breakInside: isFirstPdfTable ? 'auto' : 'avoid',
            boxShadow: 'none',
            m: 1
          }

        }}
        id={id}
        data-report-page="conversation"
      >
        <p></p>
        {!hideTitle && (
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
            <Typography variant="h6" gutterBottom>
              {title}
              {/* {typeof exportReport === "function" && exportReport()} */}
            </Typography>
            {/* {showReportCheckbox && (
            <Checkbox
              checked={isChecked}
              onChange={handleCheckboxChange}
              inputProps={{ "aria-label": "report checkbox" }}
            />
          )} */}

            {isReporting && !hideReportViewButton && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowReportModal(true)}
                style={{ textTransform: "none" }}
              >
                Report View
              </Button>
            )}
          </Stack>
        )}

        {loading && !error && <LoadingSkeleton />}

        {!loading && !error && normalizedData?.length > 0 && (
          <CardContent sx={{ padding: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: maxHeight,
                overflowY: "auto",
                overflowX: "hidden",
                "@media print": {
                  maxHeight: "none !important",
                  overflow: "visible !important",
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: "100%",
                  tableLayout: "fixed",
                }}
                aria-label="dynamic table"
              >
                {!isStringArray && (
                  <TableHead>
                    <TableRow>
                      {columnKeys.map((colKey, index) => (
                        <TableCell
                          key={index}
                          align="center"
                          sx={{
                            backgroundColor: "#4fd1c5",
                            color: "#fff",
                            fontWeight: 600,
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {formatHeader(colKey)}
                        </TableCell>
                      ))}

                      {(hasRowLevelSources || isSourcesArray) && (
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: "#4fd1c5",
                            color: "#fff",
                            fontWeight: 600,
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          Sources
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                )}

                <TableBody>
                  {normalizedData.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        backgroundColor:
                          rowIndex % 2 === 0 ? "#f5f5f5" : "#ffffff",
                      }}
                    >
                      {/* {columnKeys.map((colKey, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="center"
                        sx={{
                          wordBreak: "break-word", // wrap long words
                          whiteSpace: "normal", // allow wrapping
                        }}
                      >
                        {row[colKey] ?? "--"}
                      </TableCell>
                    ))} */}

                      {columnKeys.map((colKey, colIndex) => {
                        const isLast = colIndex === columnKeys.length - 1;
                        const baseWidth = `${100 / (columnKeys.length + 1)}%`;
                        const cellWidth = isLast
                          ? `calc(${baseWidth} * 2)`
                          : baseWidth;

                        return (
                          <TableCell
                            key={colIndex}
                            align="center"
                            sx={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              width: cellWidth,
                            }}
                          >
                            {Array.isArray(row[colKey])
                              ? row[colKey].map((val, idx) => (
                                <div key={idx}>{val}</div>
                              ))
                              : row[colKey] ?? "--"}
                          </TableCell>
                        );
                      })}

                      {/* Row-level sources - display in each row */}
                      {hasRowLevelSources && (
                        <TableCell
                          align="center"
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            verticalAlign: "top",
                          }}
                        >
                          {row.sources && Array.isArray(row.sources) && row.sources.length > 0 ? (
                            row.sources.map((src, idx) => (
                              <div key={idx} style={{ marginBottom: '8px' }}>
                                <a
                                  href={src}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {src}
                                </a>
                              </div>
                            ))
                          ) : (
                            "--"
                          )}
                        </TableCell>
                      )}

                      {/* Table-level sources - display once with rowSpan (fallback) */}
                      {!hasRowLevelSources && isSourcesArray && rowIndex === 0 && (
                        <TableCell
                          align="center"
                          rowSpan={normalizedData.length}
                          sx={{
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            verticalAlign: "top",
                          }}
                        >
                          {sources.map((src, idx) => (
                            <div key={idx} style={{ marginBottom: '8px' }}>
                              <a
                                href={src}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#1976d2",
                                  textDecoration: "underline",
                                }}
                              >
                                {src}
                              </a>
                            </div>
                          ))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        <SnippetModal handleClose={() => { setOpen(false) }} open={open} data={rawData} />
      </Card>
      <Modal
        open={showReportModal}
        handleClose={() => setShowReportModal(false)}
        title={`${title} - Report View`}
        width={1200}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", padding: 8 }} className="conversation-report-modal">
          <PaginatedGenericTable
            data={data}
            title={title}
            sources={sources}
            rawData={rawData}
            maxPageHeight={600}
            open={open}
            setOpen={setOpen}
            error={error}
            exportReport={exportReport}
            loading={loading}
          />
        </div>
      </Modal>

    </>
  );
});

export default GenericTable;
